import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, NumberInput, FormInstance, SelectPicker, CheckPicker, Modal, Button, Textarea} from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, BaseResponseModel, LatLng, PageState } from '@/models/core.model';
import PageContent from '@/components/PageContent';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import ErrorMessage from '@/components/ErrorMessage';
import { AxiosError } from 'axios';
import { ClubEventStatusEnum, ClubEventStatusList, ClubEventVisibilityEnum, ClubEventVisibilityList, RunningClubEvent, UpsertRunningClubEvent } from '@/models/running-club-event.model';
import { getRunningClubCustomRolesByClub, upsertRunningClubEvent } from '@/services/running-club-event.service';
import ZonedDatePicker from '@/components/ZonedDatePicker';
import ImageUploader from '@/components/ImageUploader';
import { ObjectStorageFileCategory } from '@/models/storage.model';
import { getCity } from '@/services/city.service';
import { City } from '@/models/city.model';
import { getRunningClub, getRunningClubsLookup } from '@/services/running-club.service';
import { RunningClubCustomRole } from '@/models/running-club-custom-role.model';
import FetchingSelect from '@/components/FetchingSelect';
import { GoogleMap, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { hk_lat_lng } from '@/constants/map';
import { getBoundingBoxCenter, getPolylinePathFromGeoJson } from '@/utils/map';

const schema = Schema.Model<UpsertRunningClubEvent>({
  id: Schema.Types.NumberType(),
  name: Schema.Types.StringType().isRequired('This field is required.'),
  startTime: Schema.Types.DateType().isRequired('This field is required.'),
  endTime: Schema.Types.DateType(),
  jsonPath: Schema.Types.StringType(),
  visibility: Schema.Types.NumberType().isRequired('This field is required.'),
  status: Schema.Types.NumberType().isRequired('This field is required.'),
  runningClubId: Schema.Types.NumberType().isRequired('This field is required.'),
});

const emptyModel: UpsertRunningClubEvent = {
  id: undefined,
  name: '',
  description: undefined,
  perks: undefined,
  coverImage: undefined,
  startTime: undefined,
  endTime: undefined,
  startLat: undefined,
  startLng: undefined,
  endLat: undefined,
  endLng: undefined,
  startAddress: undefined,
  jsonPath: undefined,
  // Always sent: an omitted non-nullable enum binds to 0 server-side and fails validation.
  visibility: ClubEventVisibilityEnum.Public,
  status: ClubEventStatusEnum.Draft,
  customRoleIds: [],
  runningClubId: undefined,
};

interface RunningClubEventFormProps{
  initValue?: UpsertRunningClubEvent;
  returnTo?: string;
  formObject?: RunningClubEvent;
  pageState: PageState
};

const RunningClubEventForm: React.FC<RunningClubEventFormProps> = ({formObject, initValue, pageState, returnTo}) => {
  const [formValue, setFormValue] = useState<UpsertRunningClubEvent>(emptyModel);
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [city, setCity] = useState<City | undefined>(undefined);
  const [customRoles, setCustomRoles] = useState<RunningClubCustomRole[]>([]);
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const [polylinePath, setPolylinePath] = useState<LatLng[]>([]);
  const [mapCenter, setMapCenter] = useState<LatLng|undefined>(hk_lat_lng);
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_MAP_API_KEY!,
  });

  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  const localStoredClubIdKey = 'last-club-id-for-club-event';
  const getLocalStoredClubId = () => {
    const stored = localStorage.getItem(localStoredClubIdKey);
    return stored ? Number(stored) : undefined;
  };
  const setLocalStoredClubId = (id: number) => {
    localStorage.setItem(localStoredClubIdKey, id.toString());
  };

  useEffect(() => {
    const localStoredClubId = getLocalStoredClubId();
    if(pageState == PageState.New){
      setFormValue({...emptyModel, ...initValue, runningClubId: initValue?.runningClubId ?? localStoredClubId});
      updateCity(initValue?.runningClubId || localStoredClubId);
      return;
    }
    
    if (formObject) {
      setFormValue(formObject);
      updatePolyline(formObject.jsonPath ?? '');
      updateCity(formObject.runningClubId);
    } else {
      setFormValue(emptyModel);
    }

  }, [formObject, initValue]);

  useEffect(() => {
    updateMapCenter();
  }, [formValue, city, polylinePath]);

  useEffect(() => {
    updatePolyline(formValue.jsonPath ?? '');
  }, [formValue.jsonPath]);

  const updateMapCenter = () => {
    if(polylinePath && polylinePath.length > 0){
      setMapCenter(getBoundingBoxCenter(polylinePath));
      return;
    }
    if(formValue?.startLat != null && formValue?.startLng != null){
      setMapCenter({lat: formValue.startLat, lng: formValue.startLng});
      return;
    }
    if(formValue?.endLat != null && formValue?.endLng != null){
      setMapCenter({lat: formValue.endLat, lng: formValue.endLng});
      return;
    }
    if(city){
      setMapCenter({lat: city.lat, lng: city.lon});
      return;
    }
    setMapCenter(hk_lat_lng);
  };

  const updateCity = (runningClubId?: number) => {
    if(runningClubId){
      getRunningClub(runningClubId).then(res => {
        getCity(res.result.cityId).then(res => setCity(res.result));
      });
      getRunningClubCustomRolesByClub(runningClubId).then(res => setCustomRoles(res.result));
    } else {
      setCity(undefined);
      setCustomRoles([]);
    }
  };

  const isNewlyCanceled = () => {
    return pageState !== PageState.New
      && formObject?.status !== ClubEventStatusEnum.Canceled
      && formValue.status === ClubEventStatusEnum.Canceled;
  };

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      console.log("errors:", formError);
      return;
    }
    if (isNewlyCanceled()) {
      setConfirmingCancel(true);
      return;
    }
    await doSubmit();
  };

  const doSubmit = async () => {
    setErrorModel(undefined);
    setLoading(true);
    try {
      const casted = {...formValue, jsonPath: hasJsonPath() ? formValue.jsonPath : undefined};
      const result: BaseResponseModel<any> = await upsertRunningClubEvent(casted);
      if (result.success) {
        setLocalStoredClubId(formValue.runningClubId!);
        if (returnTo) {
          nav(returnTo);
        } else {
          nav(`/running-clubs/${formValue.runningClubId}`, {state:{tab: "running-club-events"}});
        }
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
      setConfirmingCancel(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  const hasJsonPath = () => {
    return !!(formValue.jsonPath ?? '').trim();
  };

  const updatePolyline = (geoJson: string) : LatLng[]=> {
    const polyline = getPolylinePathFromGeoJson(geoJson);
    if(polyline && polyline.length > 0) {
      setPolylinePath(polyline);
      return polyline;
    } else { 
      setPolylinePath([]);
      return [];
    }
  };

  const handleJsonChangeChange = (geoJson: string) => {
    const polyline = updatePolyline(geoJson);

    if(polyline.length > 0){
      setFormValue(prevFormValue => ({
        ...prevFormValue,
        startLat: polyline[0].lat,
        startLng: polyline[0].lng,
        endLat: polyline[polyline.length - 1].lat,
        endLng: polyline[polyline.length - 1].lng,
      }));
    }
  };

  const handleOnMapClick = (e: google.maps.MapMouseEvent) => {
    if (hasJsonPath()) return;
    if (e.latLng) {
      const position = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };
      setFormValue(pre => {
        const startMissing = pre.startLat == null || pre.startLng == null;
        const endMissing = pre.endLat == null || pre.endLng == null;
        if (!startMissing && !endMissing) return pre;
        const next = {
          ...pre,
          startLat: startMissing ? position.lat : pre.startLat,
          startLng: startMissing ? position.lng : pre.startLng,
          endLat: (!startMissing && endMissing) ? position.lat : pre.endLat,
          endLng: (!startMissing && endMissing) ? position.lng : pre.endLng,
        };
        return next;
      });
    }
  };

  const handleMarkerDrag = (key: string, e: google.maps.MapMouseEvent) => {
    if (hasJsonPath()) return;
    if (e.latLng) {
      const position = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };

      setFormValue(pre => {
        const next = key === 'start'
          ? { ...pre, startLat: position.lat, startLng: position.lng }
          : { ...pre, endLat: position.lat, endLng: position.lng };
        return next;
      });
    }
  };

  const polylineOptions = {
    strokeColor: '#FF6F61', // coral-red color
    strokeOpacity: 0.8,
    strokeWeight: 5,
    geodesic: true,
  };

  return (
    <PageContent>
      <Form name='upsert-club-event' fluid ref={formRef}
          onChange={handleFormChange}
          onCheck={setFormError}
          formValue={formValue}
          model={schema}
          disabled={loading}>
        <Grid w='100%' fluid>
          <Row>
            <Col span={{xs:24, xl:8}}>
              <Form.Group>
                <Form.Label>Cover Image</Form.Label>
                <Form.Control
                  accepter={ImageUploader}
                  name="coverImage"
                  height="250px"
                  width="250px"
                  url={formValue.coverImage}
                  objectStorageCategory={ObjectStorageFileCategory.RunningClub}
                  onChange={url => setFormValue(pre => ({...pre, coverImage: url}))}
                />
              </Form.Group>            
            </Col>
            <Col span={{xs:24, xl:16}}>
              <Row>
                <Col span={{xs:24, xl:12}}>
                  <Form.Group>
                    <Form.Label>Running Club</Form.Label>
                    <Form.Control name="runningClubId" 
                      accepter={FetchingSelect} block
                      fetchData={(text, page) => getRunningClubsLookup(text, page)}
                      onChange={val => updateCity(val)}
                      placeholder="Select a club"
                      disabled={!!initValue?.runningClubId}
                    />
                  </Form.Group>
                </Col>
                <Col span={{xs:24, xl:12}}>
                  <Form.Group>
                    <Form.Label>Name</Form.Label>
                    <Form.Control name="name"/>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24}}>
                  <Form.Group>
                    <Form.Label>Description</Form.Label>
                    <Form.Control name="description" accepter={Textarea} rows={3} />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24}}>
                  <Form.Group>
                    <Form.Label>Perks</Form.Label>
                    <Form.Control name="perks" accepter={Textarea} rows={4} placeholder="Perks for attendees, e.g. sponsor gifts, free coffee..." />
                    <Form.HelpText>Optional. Shown to attendees; leave blank for none.</Form.HelpText>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24}}>
                  <Form.Group>
                    <Form.Label>Start Address</Form.Label>
                    <Form.Control name="startAddress" placeholder="e.g. MTR Exit B, next to the 7-Eleven" />
                    <Form.HelpText>Shown to runners as the meeting point. Leave empty to point them to the map.</Form.HelpText>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24, xl:12}}>
                  <Form.Group>
                    <Form.Label>Visibility</Form.Label>
                    <Form.Control name="visibility" accepter={SelectPicker} block
                      data={ClubEventVisibilityList} searchable={false} cleanable={false} />
                  </Form.Group>
                </Col>
                <Col span={{xs:24, xl:12}}>
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Form.Control name="status" accepter={SelectPicker} block
                      data={ClubEventStatusList} searchable={false} cleanable={false} />
                  </Form.Group>
                </Col>
              </Row>

              {formValue.visibility === ClubEventVisibilityEnum.Private && (
              <Row>
                <Col span={{xs:24}}>
                  <Form.Group>
                    <Form.Label>Visible to custom roles</Form.Label>
                    <Form.Control name="customRoleIds" accepter={CheckPicker} block
                      data={customRoles.map(r => ({ value: r.id, label: r.name }))}
                      searchable={false}
                    />
                    <Form.HelpText>
                      A private event with no roles selected is visible to club staff only.
                    </Form.HelpText>
                  </Form.Group>
                </Col>
              </Row>
              )}
              <Row>
                <Col span={{xs:24, xl:12}}>
                  <Form.Group>
                    <Form.Label>Start</Form.Label>
                    <Form.Control accepter={ZonedDatePicker} block
                      name="startTime"
                      value={formValue.startTime ?? null}
                      onChange={dt => setFormValue(pre => ({...pre, startTime: dt ?? undefined}))}
                      zoneIanaId={city?.ianaTimeZoneId || 'UTC'}
                    />
                  </Form.Group>
                </Col>
                <Col span={{xs:24, xl:12}}>
                  <Form.Group>
                    <Form.Label>End</Form.Label>
                    <Form.Control accepter={ZonedDatePicker} block
                      name="endTime"
                      value={formValue.endTime ?? null}
                      onChange={dt => setFormValue(pre => ({...pre, endTime: dt ?? undefined}))}
                      zoneIanaId={city?.ianaTimeZoneId || 'UTC' }
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Col>
          </Row>
          <Divider/>
          <Row>
            <Col span={{xs:24, xl:8}}>
              <Row>
                <Col span={{xs:24, xl:12}}>
                  <Form.Group>
                    <Form.Label>Start Latitude</Form.Label>
                    <Form.Control name="startLat" accepter={NumberInput}
                      value={formValue.startLat ?? null}
                      disabled={hasJsonPath()} />
                  </Form.Group>                  
                </Col>
                <Col span={{xs:24, xl:12}}>
                  <Form.Group>
                    <Form.Label>Start Longitude</Form.Label>
                    <Form.Control name="startLng" accepter={NumberInput}
                      value={formValue.startLng ?? null}
                      disabled={hasJsonPath()} />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24, xl:12}}>
                  <Form.Group>
                    <Form.Label>End Latitude</Form.Label>
                    <Form.Control name="endLat" accepter={NumberInput}
                      value={formValue.endLat ?? null}
                      disabled={hasJsonPath()} />
                  </Form.Group>                  
                </Col>
                <Col span={{xs:24, xl:12}}>
                  <Form.Group>
                    <Form.Label>End Longitude</Form.Label>
                    <Form.Control name="endLng" accepter={NumberInput}
                      value={formValue.endLng ?? null}
                      disabled={hasJsonPath()} />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24}}>
                  <Form.Group>
                    <Form.Label>Json Path</Form.Label>
                    <Form.Control 
                      name="jsonPath" 
                      value={formValue.jsonPath ?? ''} 
                      accepter={Textarea} 
                      rows = {25}
                      onChange={handleJsonChangeChange}
                    />
                  </Form.Group>                
                </Col>
              </Row>            
            </Col>            
            <Col span={{xs:24, xl:16}} style={{textAlign: 'center'}}>
              <Form.Group>
                <Form.Label>{
                  (formValue.startLat == null || formValue.startLng == null) ? 
                  'Click on map to select START point' : 
                  (formValue.endLat == null || formValue.endLng == null) ? 
                  'Click on map to select END point' : 
                  'Drag START or END markers'}
                </Form.Label>
                {isLoaded && <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '650px', borderRadius: 10 }}
                  center={mapCenter}
                  zoom={14}
                  onClick={handleOnMapClick}
                >
                  {polylinePath && polylinePath.length > 0 && <Polyline path={polylinePath} options={polylineOptions}/>}
                  {formValue.startLat != null && formValue.startLng != null && <Marker
                    key={'start'} 
                    label={'S'} 
                    position={{lat:formValue.startLat, lng:formValue.startLng}} 
                    onDragEnd={e => handleMarkerDrag('start', e)}
                    draggable
                  />}
                  {formValue.endLat != null && formValue.endLng != null && <Marker
                    key={'end'} 
                    label={'E'} 
                    position={{lat:formValue.endLat, lng:formValue.endLng}} 
                    onDragEnd={e => handleMarkerDrag('end', e)}
                    draggable
                  />}
                </GoogleMap>}
              </Form.Group>
            </Col>          
          </Row>
        </Grid>
      </Form>
      <Divider />
      {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}
      
      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => console.log(formValue)}> 
          Back
        </IconButton>
        <IconButton
          icon={<PageNextIcon />}
          placement="right"
          appearance="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </IconButton>
      </Stack>

      <Modal open={confirmingCancel} onClose={() => setConfirmingCancel(false)} size="xs">
        <Modal.Header><Modal.Title>Cancel this event?</Modal.Title></Modal.Header>
        <Modal.Body>
          Setting status to Canceled will void every check-in for this event, delete the
          activity records they generated, and unlink any real activities associated with it.
          This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setConfirmingCancel(false)} appearance="subtle">Back</Button>
          <Button onClick={doSubmit} appearance="primary" color="red" loading={loading}>
            Cancel event
          </Button>
        </Modal.Footer>
      </Modal>
    </PageContent>
  );
};

export default RunningClubEventForm;
